import cookie from './CookieStorage';
import storageTypes from '../constants/storageTypes';

interface StorageInterface {
  setItem(key: string, value: string): void;
  getItem(key: string): string | null;
  removeItem(key: string): void;
  clear(): void;
}

interface StorageAvailability {
  [key: string]: boolean;
}

interface PossibleStorages {
  [key: string]: StorageInterface | null;
}

const cookieStorage: StorageInterface = {
  ...cookie,
  clear: () => {}
};

class StorageManager {
  private storageAvailability: StorageAvailability;
  private storage: StorageInterface | null;
  private possibleStorages: PossibleStorages;

  constructor() {
    this.storageAvailability = {};
    this.storage = null;
    this.possibleStorages = {
      [storageTypes.local_storage]: this.getStorageApi('localStorage'),
      [storageTypes.cookies]: cookieStorage,
      [storageTypes.session_storage]: this.getStorageApi('sessionStorage')
    };
    this.initStorage();
  }

  private getStorageApi = (
    type: 'localStorage' | 'sessionStorage'
  ): StorageInterface | null => {
    if (['sessionStorage', 'localStorage'].includes(type)) {
      try {
        return window[type];
      } catch (ex) {
        return null;
      }
    }
    return null;
  };

  private isStorageAvailable = (storageType: string): boolean => {
    const storage = this.possibleStorages[storageType];
    if (!storage) {
      return false;
    }

    if (
      Object.prototype.hasOwnProperty.call(
        this.storageAvailability,
        storageType
      )
    ) {
      return this.storageAvailability[storageType];
    }

    try {
      const testKey = 'test';
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      this.storageAvailability[storageType] = true;
    } catch (error) {
      this.storageAvailability[storageType] = false;
    }

    return this.storageAvailability[storageType];
  };

  public checkStorageAvailability = (storageType: string): boolean =>
    !!this.possibleStorages[storageType] &&
    this.isStorageAvailable(storageType);

  private initStorage = (): void => {
    if (this.storage) {
      return;
    }

    Object.keys(this.possibleStorages).every(type => {
      if (this.checkStorageAvailability(type)) {
        this.storage = this.possibleStorages[type];
        return false;
      }
      return true;
    });
  };

  private getStorage = (): StorageInterface => {
    if (this.storage) {
      return this.storage;
    }
    throw Error('No available storage');
  };

  public set = (key: string, value: unknown): void => {
    this.getStorage().setItem(key, JSON.stringify(value));
  };

  public get = (key: string): unknown =>
    JSON.parse(this.getStorage().getItem(key) || 'null');

  public clear = (key?: string): void => {
    if (key) {
      this.getStorage().removeItem(key);
    } else {
      this.getStorage().clear();
    }
  };
}

const storageManager = new StorageManager();

export default {
  checkStorageAvailability: storageManager.checkStorageAvailability,
  get: storageManager.get,
  set: storageManager.set,
  clear: storageManager.clear
} as const;
